import React, { useState, useRef, useEffect } from "react";
import "./AIAssistant.css";

function AIAssistant() {
  const [messages, setMessages] = useState([
    {
      type: "assistant",
      content:
        "👋 Hello! I'm your Restaurant Analytics AI Assistant. I can help you understand your data and provide insights about your restaurant's performance.",
      timestamp: new Date(),
    },
    {
      type: "assistant",
      content:
        '💡 Try asking me questions like:\n• "What is our total profit?"\n• "Show me our top selling items"\n• "What are our sales trends?"\n• "Give me recommendations for improvement"',
      timestamp: new Date(),
    },
    {
      type: "assistant",
      content:
        "📋 Note: Make sure you have uploaded your sales data and product costs first. I'll automatically analyze your data when you ask your first question!",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sampleQuestions = [
    "What is our total profit?",
    "Show me our top selling items",
    "What are our sales trends?",
    "Are there any red flags in our data?",
    "Give me recommendations for improvement",
    "How is our product performance?",
    "What is our operational efficiency?",
    "Tell me about our profit margins",
  ];

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      type: "user",
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      // First, try to ask the AI assistant
      let response = await fetch(
        "https://restaurant-analytics-backend.onrender.com/ai/ask",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question: inputMessage,
          }),
        }
      );

      let data = await response.json();

      // If we get a 400 error about no analyzed data, try to run analysis first
      if (
        response.status === 400 &&
        data.detail &&
        data.detail.includes("No analyzed data available")
      ) {
        const analysisMessage = {
          type: "assistant",
          content:
            "🔄 I need to analyze your data first. Let me do that for you...",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, analysisMessage]);

        // Run the analysis endpoint
        const analysisResponse = await fetch(
          "https://restaurant-analytics-backend.onrender.com/analyze",
          {
            method: "POST",
          }
        );

        if (analysisResponse.ok) {
          // Analysis successful, now try the AI query again
          response = await fetch(
            "https://restaurant-analytics-backend.onrender.com/ai/ask",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                question: inputMessage,
              }),
            }
          );
          data = await response.json();
        } else {
          const analysisError = await analysisResponse.json();
          throw new Error(
            analysisError.detail ||
              "Failed to analyze data. Please make sure you have uploaded sales and cost data."
          );
        }
      }

      if (response.ok) {
        const assistantMessage = {
          type: "assistant",
          content:
            data.answer ||
            "I apologize, but I couldn't process your request. Please make sure you have uploaded your data first.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        throw new Error(
          data.detail || "Failed to get response from AI assistant"
        );
      }
    } catch (error) {
      console.error("AI Assistant Error:", error);
      const errorMessage = {
        type: "assistant",
        content: `❌ ${
          error.message ||
          "Sorry, I encountered an error while processing your request. Please make sure you have uploaded your sales data and product costs first."
        }`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSampleQuestion = (question) => {
    setInputMessage(question);
  };

  const formatMessage = (content) => {
    // Simple formatting for bullet points and line breaks
    return content.split("\n").map((line, index) => (
      <div key={index} className="message-line">
        {line.trim()}
      </div>
    ));
  };

  return (
    <div className="ai-assistant">
      <div className="ai-header">
        <h1>🤖 AI Analytics Assistant</h1>
        <p>Ask me anything about your restaurant data in natural language</p>
      </div>

      <div className="chat-container">
        <div className="messages-container">
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.type}`}>
              <div className="message-avatar">
                {message.type === "user" ? "👤" : "🤖"}
              </div>
              <div className="message-content">
                <div className="message-text">
                  {formatMessage(message.content)}
                </div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message assistant loading">
              <div className="message-avatar">🤖</div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="message-text">Analyzing your data...</div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="sample-questions">
          <h3>💡 Try these sample questions:</h3>
          <div className="questions-grid">
            {sampleQuestions.map((question, index) => (
              <button
                key={index}
                className="sample-question-btn"
                onClick={() => handleSampleQuestion(question)}
                disabled={isLoading}
              >
                {question}
              </button>
            ))}
          </div>
        </div>

        <div className="input-container">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about your restaurant data..."
            disabled={isLoading}
            rows="2"
            className="message-input"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="send-button"
          >
            {isLoading ? "⏳" : "📤"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AIAssistant;
